import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { BrandMark } from '@/shared/ui';
import { isPortalApiError } from '../api/portalProblem';
import { useAcceptRepInvite } from './inviteAcceptApi';

type Status = 'accepting' | 'success' | 'already_used' | 'error' | 'missing_token';

/**
 * The Portal's rep-invite-accept landing page (PP-9.4 D20) -- the page a rep
 * lands on after clicking their emailed accept link
 * (`{portal_frontend_base_url}/invite-accept?token=...`, see
 * `app/config.py::portal_rep_invite_accept_url` on the opportunity side).
 * No session of any kind — the token itself, in the query string, IS the
 * credential, verified entirely server-side.
 *
 * Auto-submits once on mount (a rep clicking an email link expects the
 * accept to just happen, not a second button to press) — guarded by a ref,
 * not a dependency-array trick, since StrictMode's dev-only double-invoke
 * would otherwise burn the token's single use against the real backend on
 * the very first render.
 */
export function InviteAcceptPage(): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accept = useAcceptRepInvite();
  const hasSubmitted = useRef(false);

  const token = searchParams.get('token');
  const [status, setStatus] = useState<Status>(token ? 'accepting' : 'missing_token');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token || hasSubmitted.current) return;
    hasSubmitted.current = true;
    accept.mutate(
      { token },
      {
        onSuccess: (data) => {
          setStatus('success');
          setMessage(data.detail);
        },
        onError: (error) => {
          if (isPortalApiError(error) && error.status === 409) {
            setStatus('already_used');
            setMessage(error.detail ?? 'This invite has already been used.');
            return;
          }
          setStatus('error');
          setMessage(
            isPortalApiError(error) && error.detail
              ? error.detail
              : 'Something went wrong. Please try again.',
          );
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally fires once on mount
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0f1215 0%, #1a2230 100%)'
            : 'linear-gradient(135deg, #eef2f7 0%, #ffffff 100%)',
      }}
    >
      <Paper
        elevation={3}
        sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 2 }}
      >
        <Stack spacing={3} alignItems="center">
          <BrandMark />
          <Typography variant="h5" component="h1" textAlign="center">
            Partner Deals Portal
          </Typography>

          {status === 'accepting' ? (
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={32} aria-label="Accepting your invite" />
              <Typography variant="body2" color="text.secondary">
                Accepting your invite…
              </Typography>
            </Stack>
          ) : null}

          {status === 'missing_token' ? (
            <Alert severity="error" sx={{ width: '100%' }}>
              This link is missing its invite token. Please use the link from your invite
              email exactly as sent.
            </Alert>
          ) : null}

          {status === 'success' ? (
            <>
              <Alert severity="success" sx={{ width: '100%' }}>
                {message}
              </Alert>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                You can now sign in with the email address this invite was sent to.
              </Typography>
              <Button
                variant="contained"
                size="large"
                fullWidth
                sx={{ minHeight: 44 }}
                onClick={() => navigate('/sign-in', { replace: true })}
              >
                Go to sign-in
              </Button>
            </>
          ) : null}

          {status === 'already_used' ? (
            <>
              <Alert severity="info" sx={{ width: '100%' }}>
                {message}
              </Alert>
              <Button
                variant="contained"
                size="large"
                fullWidth
                sx={{ minHeight: 44 }}
                onClick={() => navigate('/sign-in', { replace: true })}
              >
                Go to sign-in
              </Button>
            </>
          ) : null}

          {status === 'error' ? (
            <Alert severity="error" sx={{ width: '100%' }}>
              {message}
            </Alert>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
